import React, { useEffect, useState } from 'react';
import { Button, Col, FormGroup, Label, Row, Input } from 'reactstrap';
import { TableColumn, TableColumnType } from '@g17eco/types/universalTracker';
import { SingleSelect as Select } from '@g17eco/molecules/select/SingleSelect';
import { Option, SelectTypes } from '@g17eco/molecules/select/types';
import { getUnitsForType, UnitTypes } from '@utils/units';
import { UnitOption } from '@features/units-currency-settings';
import { useGetValueListsQuery } from '@api/value-list';
import { ValueListPlain } from '@g17eco/types/valueList';
import { ValueListModal } from './ValueListModal';
import { CODE_REGEX, CODE_REGEX_ERROR } from '@utils/string';
import { SelectFactory } from '@g17eco/molecules/select/SelectFactory';
import { checkIsValueListColumn } from '@utils/universalTracker';

interface Props {
  initialData?: TableColumn;
  onSave: (column: TableColumn) => void;
  onCancel: () => void;
  existingCodes: string[];
  readOnly?: boolean;
  initiativeId: string;
}

const EXTENDED_COLUMN_TYPES: Option[] = [
  { value: TableColumnType.Number, label: 'Number' },
  { value: TableColumnType.Text, label: 'Text' },
  { value: TableColumnType.Date, label: 'Date' },
  { value: TableColumnType.Percentage, label: 'Percentage' },
  { value: TableColumnType.ValueList, label: 'Value List (Single)' },
  { value: TableColumnType.ValueListMulti, label: 'Value List (Multiple)' },
];

const getOption = (options: Option[], value: string | undefined) => options.find((o) => o.value === value) || null;

const unitTypeOptions = [
  { value: '', label: 'None' },
  { value: 'time', label: 'Time' },
  { value: 'area', label: 'Area' },
  { value: 'length', label: 'Length' },
  { value: 'mass', label: 'Mass' },
  { value: 'volume', label: 'Volume' },
  { value: 'energy', label: 'Energy' },
  { value: 'co2Emissions', label: 'CO2 Emissions' },
  { value: 'partsPer', label: 'Concentration/PartsPer' },
  { value: 'currency', label: 'Currency' },
];

const getUnitOptions = (unitType: string | undefined) => {
  if (!unitType) return [];
  const type = unitType as keyof typeof UnitTypes;
  return (getUnitsForType(type) || []).map((u: UnitOption) => ({ label: `${u.singular} (${u.abbr})`, value: u.abbr }));
};

const getNumberScaleOptions = () => {
  return getUnitsForType(UnitTypes.currency).map(({ abbr, singular }: UnitOption) => ({
    label: `${singular} (${abbr})`,
    value: abbr,
  }));
};

const CREATE_NEW_VALUE_LIST_OPTION = '__create_new__';

interface TableColumnFormData extends Partial<TableColumn> {
  type: TableColumnType;
}

export const TableColumnForm = ({
  initialData,
  onSave,
  onCancel,
  existingCodes,
  readOnly = false,
  initiativeId,
}: Props) => {
  const [formData, setFormData] = useState<TableColumnFormData>({
    code: '',
    name: '',
    type: TableColumnType.Number,
    shortName: '',
    instructions: '',
    unitType: '',
    unit: '',
    numberScale: '',
    listId: undefined,
    validation: {
      min: undefined,
      max: undefined,
      decimal: undefined,
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showValueListModal, setShowValueListModal] = useState(false);

  const { data: valueLists = [] } = useGetValueListsQuery({ ownerId: initiativeId });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        validation: initialData.validation || {},
      });
    }
  }, [initialData]);

  const handleChange = (field: keyof TableColumn, value: string | undefined | null) => {
    setFormData((prev) => {
      const updates: Partial<TableColumn> = { [field]: value };

      // Clear irrelevant fields when type changes
      if (field === 'type') {
        const isNumeric = value === TableColumnType.Number || value === TableColumnType.Percentage;
        const isValueList = value === TableColumnType.ValueList || value === TableColumnType.ValueListMulti;

        if (!isNumeric) {
          updates.unitType = '';
          updates.unit = '';
          updates.numberScale = '';
          updates.validation = { ...prev.validation, min: undefined, max: undefined, decimal: undefined };
        }

        if (!isValueList) {
          updates.listId = undefined;
        }
      }

      // Clear unit if unitType changes
      if (field === 'unitType') {
        updates.unit = '';
      }

      return { ...prev, ...updates };
    });

    if (field === 'code') {
      if (!value) {
        setErrors((prev) => ({ ...prev, code: 'Code is required' }));
      } else if (!CODE_REGEX.test(value)) {
        setErrors((prev) => ({ ...prev, code: CODE_REGEX_ERROR }));
      } else if (existingCodes.includes(value) && value !== initialData?.code) {
        setErrors((prev) => ({ ...prev, code: 'Code must be unique' }));
      } else {
        setErrors((prev) => ({ ...prev, code: '' }));
      }
    } else if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleListIdChange = (value: string | undefined | null) => {
    if (value === CREATE_NEW_VALUE_LIST_OPTION) {
      setFormData((prev) => ({ ...prev, listId: undefined }));
      setShowValueListModal(true);
      return;
    }
    setFormData((prev) => ({ ...prev, listId: value || undefined }));
    if (errors.listId) {
      setErrors((prev) => ({ ...prev, listId: '' }));
    }
  };

  const handleValueListSuccess = (valueList: ValueListPlain) => {
    setShowValueListModal(false);
    // For create mode (no selectedValueList), update the selected listId
    if (!selectedValueList) {
      setFormData((prev) => ({ ...prev, listId: valueList._id }));
    }
  };

  const handleValidationChange = (field: 'min' | 'max' | 'decimal', value: string) => {
    const val = value === '' ? undefined : Number(value);
    setFormData((prev) => ({
      ...prev,
      validation: {
        ...prev.validation,
        [field]: val,
      },
    }));

    // Clear validation errors when values change
    if (field === 'min' || field === 'max') {
      setErrors((prev) => ({ ...prev, min: '' }));
    }
  };

  const isNumber = formData.type === TableColumnType.Number;
  const isNumeric = formData.type === TableColumnType.Number || formData.type === TableColumnType.Percentage;
  const isValueList = checkIsValueListColumn(formData);

  const unitOptions = getUnitOptions(formData.unitType);
  const numberScaleOptions = getNumberScaleOptions();

  // Build value list options for the dropdown
  const valueListOptions: Option[] = [
    {
      value: CREATE_NEW_VALUE_LIST_OPTION,
      label: (
        <span className='text-primary'>
          <i className='fa fa-plus text-primary' /> Create new value list...
        </span>
      ),
    },
    ...valueLists.map((vl: ValueListPlain) => ({
      value: vl._id,
      label: vl.name,
    })),
  ];

  // Get selected value list details (from company lists only)
  const selectedValueList = valueLists.find((vl: ValueListPlain) => vl._id === formData.listId);

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.code) newErrors.code = 'Code is required';
    if (!initialData && existingCodes.includes(formData.code || '')) newErrors.code = 'Code must be unique';
    if (!formData.name) newErrors.name = 'Name is required';

    if (isNumeric) {
      const min = formData.validation?.min;
      const max = formData.validation?.max;
      if (min !== undefined && max !== undefined && min >= max) {
        newErrors.min = 'Min must be less than Max';
      }
    }

    if (isValueList && !formData.listId) {
      newErrors.listId = 'Please select a value list';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(formData as TableColumn);
    }
  };

  const isSaveDisabled =
    !formData.code ||
    !formData.name ||
    (isValueList && !formData.listId) ||
    Object.values(errors).some((error) => !!error);

  return (
    <div className='bg-white p-4 shadow-sm rounded'>
      <h3 className='mb-4'>{initialData ? 'Edit table column' : 'Create table column'}</h3>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label className='fw-bold'>
              Code <span className='text-danger'>*</span>
            </Label>
            <Input
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value)}
              disabled={!!initialData || readOnly}
              placeholder='Enter a code'
              invalid={!!errors.code}
            />
            {errors.code && <div className='text-danger small mt-1'>{errors.code}</div>}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label className='fw-bold'>Type</Label>
            <Select
              options={EXTENDED_COLUMN_TYPES}
              value={getOption(EXTENDED_COLUMN_TYPES, formData.type)}
              onChange={(val) => handleChange('type', val?.value)}
              placeholder='Select type'
              isDisabled={readOnly}
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label className='fw-bold'>
          Column name <span className='text-danger'>*</span>
        </Label>
        <Input
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          placeholder='Enter a name'
          invalid={!!errors.name}
          disabled={readOnly}
        />
        {errors.name && <div className='text-danger small mt-1'>{errors.name}</div>}
      </FormGroup>

      <FormGroup>
        <Label className='fw-bold'>Column short name</Label>
        <Input
          value={formData.shortName}
          onChange={(e) => handleChange('shortName', e.target.value)}
          placeholder='Enter a shortname'
          disabled={readOnly}
        />
      </FormGroup>

      <FormGroup>
        <Label className='fw-bold'>Column instruction (optional)</Label>
        <Input
          type='textarea'
          value={formData.instructions}
          onChange={(e) => handleChange('instructions', e.target.value)}
          placeholder='Enter instructions'
          disabled={readOnly}
        />
      </FormGroup>

      {isValueList && (
        <>
          <FormGroup>
            <Label className='fw-bold'>
              Value List <span className='text-danger'>*</span>
            </Label>
            <SelectFactory
              // Force re-render so dropdown resets after modal closes
              key={`ValueListSelect-${showValueListModal ? 'modal-open' : 'closed'}`}
              selectType={SelectTypes.SingleSelect}
              options={valueListOptions}
              value={getOption(valueListOptions, formData.listId)}
              onChange={(val) => handleListIdChange(val?.value)}
              placeholder='Select a value list'
              isDisabled={readOnly}
            />
            {errors.listId && <div className='text-danger small mt-1'>{errors.listId}</div>}
          </FormGroup>

          {selectedValueList && (
            <FormGroup>
              <div className='border rounded'>
                <div className='px-3 py-2 border-bottom fw-bold text-uppercase small background-ThemeBgToolbar'>
                  Option
                </div>
                <div className='overflow-auto' style={{ maxHeight: '240px' }}>
                  {selectedValueList.options.length === 0 ? (
                    <div className='p-3 text-muted'>No options defined</div>
                  ) : (
                    <div className='p-0'>
                      {selectedValueList.options.map((opt, index) => (
                        <div
                          key={opt.code}
                          className={`px-3 py-2${index < selectedValueList.options.length - 1 ? ' border-bottom' : ''}`}
                        >
                          {opt.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className='d-flex justify-content-end mt-2'>
                <Button className='rounded px-2 py-1 small' onClick={() => setShowValueListModal(true)}>
                  Manage option
                </Button>
              </div>
            </FormGroup>
          )}

          <FormGroup check className='mt-3'>
            <Input
              type='checkbox'
              id='allow-custom-options'
              checked={formData.validation?.allowCustomOptions ?? false}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  validation: {
                    ...prev.validation,
                    allowCustomOptions: e.target.checked,
                  },
                }));
              }}
              disabled={readOnly}
            />
            <Label check htmlFor='allow-custom-options'>
              Allow custom options
            </Label>
          </FormGroup>
        </>
      )}

      {isNumber && (
        <>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className='fw-bold'>Unit type</Label>
                <Select
                  options={unitTypeOptions}
                  value={getOption(unitTypeOptions, formData.unitType)}
                  onChange={(val) => handleChange('unitType', val?.value)}
                  placeholder='Select unit type'
                  isDisabled={readOnly}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className='fw-bold'>Unit</Label>
                <Select
                  options={unitOptions}
                  value={getOption(unitOptions, formData.unit)}
                  onChange={(val) => handleChange('unit', val?.value)}
                  placeholder='Select unit'
                  isDisabled={!formData.unitType || readOnly}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label className='fw-bold'>Number scale</Label>
            <Select
              options={numberScaleOptions}
              value={getOption(numberScaleOptions, formData.numberScale)}
              onChange={(val) => handleChange('numberScale', val?.value)}
              placeholder='Select number scale'
              isDisabled={readOnly}
            />
          </FormGroup>
        </>
      )}

      {isNumeric && (
        <>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label className='fw-bold'>Minimum number</Label>
                <Input
                  type='number'
                  value={formData.validation?.min ?? ''}
                  onChange={(e) => handleValidationChange('min', e.target.value)}
                  placeholder='0'
                  invalid={!!errors.min}
                  disabled={readOnly}
                />
                {errors.min && <div className='text-danger small mt-1'>{errors.min}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label className='fw-bold'>Maximum number</Label>
                <Input
                  type='number'
                  value={formData.validation?.max ?? ''}
                  onChange={(e) => handleValidationChange('max', e.target.value)}
                  placeholder='0'
                  disabled={readOnly}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label className='fw-bold'>Decimal point limit</Label>
            <Input
              type='number'
              value={formData.validation?.decimal ?? ''}
              onChange={(e) => handleValidationChange('decimal', e.target.value)}
              placeholder='0'
              min={0}
              max={5}
              disabled={readOnly}
            />
          </FormGroup>
        </>
      )}

      <div className='d-flex justify-content-end mt-4 pt-3'>
        <Button color='link-secondary' onClick={onCancel} className='mr-3'>
          Cancel
        </Button>
        <Button color='primary' onClick={handleSave} disabled={isSaveDisabled || readOnly}>
          Save
        </Button>
      </div>

      <ValueListModal
        key={`value-list-modal-${showValueListModal}`}
        isOpen={showValueListModal}
        onClose={() => setShowValueListModal(false)}
        initiativeId={initiativeId}
        valueList={selectedValueList}
        onSuccess={handleValueListSuccess}
      />
    </div>
  );
};
